// Engine scheduler — the pure core of the daily session engine.
//
// Everything here is a function of its arguments: content bundle in, member
// state in, config in, LOCAL date key in → queue / state transitions out. No
// Firestore, no clock reads, no imports — the same properties that let
// drillScheduler.js be simulation-tested via a data: import apply here, and
// they are load-bearing: the Home spec's hard rule is that the scheduler must
// be DETERMINISTIC per (member, local-date) so the hero's queue preview and
// the session contents can never disagree. Determinism lives here or nowhere.
//
// Spec anchors (docs/engine/): Session Engine Spec §1 (locked model), §3
// (tier rule), §4 (3-try write scoring); Home Progression Spec §1.4 (pacing),
// §2.2 (high-water mark), §4.11 (backlog), §4.15 (streak freeze); brief §5
// (pilot parameters as config, day boundary = device-local midnight), §6
// (interruption rules).

/** Interval ladder in days, indexed by stageIndex: learn-day, 3d, 10d, 30d. */
export const INTERVALS = [0, 3, 10, 30];

/**
 * Pilot pacing parameters (brief §5) — the BAKED DEFAULTS. The live values
 * come from appData/engineConfig via resolveConfig(); these apply when the
 * doc is missing or partial, so the engine never stalls on config.
 */
export const DEFAULT_CONFIG = { newPerDay: 1, firstSessionNew: 2, reviewCap: 5 };

/** Overlay a raw config doc onto the defaults, keeping only sane values. */
export function resolveConfig(raw) {
  const cfg = { ...DEFAULT_CONFIG };
  if (raw && typeof raw === 'object') {
    for (const k of Object.keys(DEFAULT_CONFIG)) {
      const v = raw[k];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 20) cfg[k] = Math.floor(v);
    }
  }
  return cfg;
}

// ── Local-day arithmetic ────────────────────────────────────────────────────
// Day boundary is the member's DEVICE-LOCAL midnight (brief §5, G5 accepted).
// Date keys are "YYYY-MM-DD" strings stamped from the local clock at write
// time; all arithmetic goes through Date at local noon so DST shifts can
// never move a key across a day boundary.

/** Local "YYYY-MM-DD" for a ms timestamp. */
export function dateKeyOf(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function atNoon(dateKey) {
  const [y, m, d] = String(dateKey).split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** dateKey + n calendar days → dateKey. */
export function addDays(dateKey, n) {
  const d = atNoon(dateKey);
  d.setDate(d.getDate() + n);
  return dateKeyOf(d.getTime());
}

/** Calendar days from `fromKey` to `toKey` (positive when toKey is later). */
export function dayDiff(fromKey, toKey) {
  return Math.round((atNoon(toKey) - atNoon(fromKey)) / 86400000);
}

/** "YYYY-MM" of a dateKey — the streak-freeze grant period. */
export function monthOf(dateKey) {
  return String(dateKey).slice(0, 7);
}

// ── Due-date fuzzing (engine gap A4, ruled by Home spec §1.2) ───────────────
// Applied when a due date is WRITTEN, never at queue time — queue assembly
// must stay a pure read. Deterministic per (expression, stage) so a rewrite
// of the same transition lands on the same date. The 3-day first check is
// never fuzzed: it is the pedagogical anchor the completion card promises
// (오늘 → 3일 → 10일 → 30일); only the long maintenance intervals spread.

/** Deterministic fuzz in days: 0 for stages 0–1, ±1 for 10d, ±2 for 30d. */
export function fuzzOffset(recId, stageIndex) {
  if (stageIndex < 2) return 0;
  let h = 0;
  const s = `${recId}#${stageIndex}`;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return stageIndex === 2 ? (h % 3) - 1 : (h % 5) - 2;
}

// ── Review tier (Session Engine Spec §3, locked) ────────────────────────────
// Deep when unproven or graduating: first review, missed last time, or the
// 30-day step. Otherwise light. "Write when unproven or graduating; cloze
// when maintaining."

/** 'deep' | 'light' for a due expression's state doc. */
export function tierFor(st) {
  if (!st) return 'deep';
  if ((st.reviewCount || 0) === 0) return 'deep';
  if (st.lastClean === false) return 'deep';
  if (st.stageIndex === 3) return 'deep';
  return 'light';
}

// ── Spine order (Home spec §1.4) ────────────────────────────────────────────
// domain order → situation sequence → pathOrder (the Grid-view order frozen
// into the bundle at sync time). Only authored expressions are teachable; a
// situation is fully taught before the next situation's first expression.

/** Authored expression ids in teaching order. */
export function spineOrder(content) {
  const domainOrder = new Map((content.domains || []).map((d) => [d.slug, d.order]));
  return (content.expressions || [])
    .slice()
    .sort((a, b) => {
      const sa = sitOf(content, a.situationId);
      const sb = sitOf(content, b.situationId);
      return (domainOrder.get(sa.domainSlug) || 99) - (domainOrder.get(sb.domainSlug) || 99)
        || sa.sequence - sb.sequence
        || a.pathOrder - b.pathOrder
        || String(a.id).localeCompare(String(b.id));
    })
    .map((e) => e.id);
}

function sitOf(content, situationId) {
  if (!content._sitById) {
    Object.defineProperty(content, '_sitById', {
      value: new Map((content.situations || []).map((s) => [s.id, s])),
      enumerable: false,
      configurable: true,
    });
  }
  return content._sitById.get(situationId) || { domainSlug: '', sequence: 99 };
}

/** An expression counts as taught once a NEW loop completed (learnedAt set) —
 *  or when it entered directly in review state (seeded, taught elsewhere).
 *  The one definition of "taught" that the queue, the map count and the
 *  tier all read — so they can never disagree about it. */
export function isKnown(st) {
  return !!st && (st.learnedAt != null || st.state === 'review' || st.state === 'learning');
}

// ── Queue assembly ──────────────────────────────────────────────────────────

/**
 * Reviews due on `dateKey`, ordered: missed-last-time first (they jump the
 * queue, brief §5), then most-overdue, then recId for a stable total order.
 * Uncapped — callers cap with config.reviewCap. A yesterday-missed item is
 * just a due review like any other, so it DOES count against the cap (A7).
 */
export function dueReviews(states, dateKey) {
  return Object.entries(states || {})
    .filter(([, st]) => isKnown(st) && st.dueDate != null && st.dueDate <= dateKey)
    .sort(([ida, a], [idb, b]) => {
      const missA = a.lastClean === false ? 0 : 1;
      const missB = b.lastClean === false ? 0 : 1;
      return missA - missB
        || String(a.dueDate).localeCompare(String(b.dueDate))
        || ida.localeCompare(idb);
    })
    .map(([id, st]) => ({ id, st }));
}

/**
 * Assemble the day's queue. Pure and deterministic: calling it twice with the
 * same inputs yields the same queue, which is what lets the hero preview and
 * the session agree without persisting a plan.
 *
 * @returns {{ items: Array<{id, kind, tier}>, newCount, reviewCount,
 *             dueCount, firstRun, situationId, saturated }}
 *   dueCount = due reviews BEFORE the cap (streak rule input; never shown to
 *   members — no due/backlog count ever renders, brief §5).
 */
export function assembleQueue({ content, states, config, dateKey, lastCompletedDate }) {
  const cfg = resolveConfig(config);
  const known = Object.values(states || {}).filter(isKnown);
  const firstRun = known.length === 0;

  const due = dueReviews(states, dateKey);
  const reviews = due.slice(0, cfg.reviewCap)
    .map(({ id, st }) => ({ id, kind: 'review', tier: tierFor(st) }));

  // 1 new/day is the habit's spine (Home spec §1.4) and it is enforced HERE,
  // not by the home hero: once a session finished today, a direct /session
  // revisit serves no further new items — only whatever reviews are due.
  const spent = lastCompletedDate === dateKey;
  const newBudget = spent ? 0 : (firstRun ? cfg.firstSessionNew : cfg.newPerDay);
  const fresh = [];
  if (newBudget > 0) {
    for (const id of spineOrder(content)) {
      if (fresh.length >= newBudget) break;
      const st = (states || {})[id];
      if (!isKnown(st)) fresh.push({ id, kind: 'new', tier: null });
    }
  }

  const items = [...fresh, ...reviews];
  const firstNew = fresh.length
    ? (content.expressions || []).find((e) => e.id === fresh[0].id)
    : null;
  return {
    items,
    newCount: fresh.length,
    reviewCount: reviews.length,
    dueCount: due.length,
    firstRun,
    situationId: firstNew ? firstNew.situationId : null,
    // Saturated (Home spec §4.9): nothing new left to teach AND nothing due.
    saturated: fresh.length === 0 && due.length === 0,
  };
}

// ── State transitions (Session Engine Spec §4 scoring, locked) ──────────────
// attempts: 1..3 = which submit succeeded; 4 = failed all three and typed the
// revealed answer. Display and scheduling are decoupled — every correct shows
// 정답, only attempts === 1 is "clean" and advances the interval.

/** Fresh state doc for an expression completing its NEW teach loop. */
export function applyNewOutcome({ expr, attempts, dateKey, nowMs }) {
  const clean = attempts === 1;
  return {
    state: 'learning',
    stageIndex: 1,
    // §4 scoring applies to the teach loop's write too: clean → the 3d check;
    // dirty (try 2/3 or the reveal) → TOMORROW, where the tier rule serves it
    // deep (reviewCount 0). The completion card mirrors this (내일 next).
    dueDate: addDays(dateKey, clean ? INTERVALS[1] : 1),
    learnedAt: nowMs,
    lastSeenAt: nowMs,
    lastTier: 'new',
    lastClean: clean,
    missCount: 0,
    consecutiveClean: 0,
    attemptsLast: attempts,
    reviewCount: 0,
    maxStageReached: 1, // scheduled to the 3d check — HWM scale per Home spec §2.2
    seededFrom: null,
    expressionEn: expr.expression_en,
    situationId: expr.situationId,
    domain: expr.domain,
  };
}

/**
 * Apply a review outcome to an expression's state doc → the next state doc.
 * Clean advances one step (3d → 10d → 30d, fuzzed on the long steps); a miss
 * reschedules to TOMORROW with the stage unchanged — no interval demotion,
 * ever (Home spec §4.11), and maxStageReached is monotonic by construction.
 *
 * Post-30-day policy (A6, RULED 2026-08-14): a clean 30d review stays at
 * stage 3 on a fuzzed 30-day maintenance cadence indefinitely, and
 * maxStageReached caps at 3 — there is no graduation out of rotation.
 */
export function applyReviewOutcome({ state, tier, attempts, dateKey, recId, nowMs }) {
  const clean = attempts === 1;
  const next = { ...state };
  next.state = 'review';
  next.lastSeenAt = nowMs;
  next.lastTier = tier;
  next.lastClean = clean;
  next.attemptsLast = attempts;
  next.reviewCount = (state.reviewCount || 0) + 1;
  if (clean) {
    const stage = Math.min(3, (state.stageIndex || 1) + 1);
    next.stageIndex = stage;
    next.dueDate = addDays(dateKey, INTERVALS[stage] + fuzzOffset(recId, stage));
    next.consecutiveClean = (state.consecutiveClean || 0) + 1;
    next.maxStageReached = Math.max(state.maxStageReached || 0, stage);
  } else {
    next.stageIndex = state.stageIndex || 1;
    next.dueDate = addDays(dateKey, 1);
    next.consecutiveClean = 0;
    next.missCount = (state.missCount || 0) + 1;
    next.maxStageReached = state.maxStageReached || 0;
  }
  return next;
}

// ── Situation aggregate (Home spec §2.2, §5) ────────────────────────────────
// Node state is DERIVED from these fields client-side; nothing stores a state
// string. Recomputed from the full expression-state map on every commit —
// self-healing over increment-drift. completedAt latches (never cleared):
// every input is monotonic, so regression is structurally impossible (R5).

/**
 * @returns {{ doc: object, justCompleted: boolean }} — `doc` replaces the
 * situation document wholesale except `playlist`, which is preserved from
 * `prev` (the clip layer owns it; PR E writes it).
 */
export function situationAggregate({ situation, states, prev, nowMs }) {
  const ids = situation.expressionIds || [];
  let taught = 0;
  let hwm2 = 0;
  for (const id of ids) {
    const st = (states || {})[id];
    if (isKnown(st)) taught += 1;
    if (st && (st.maxStageReached || 0) >= 2) hwm2 += 1;
  }
  const wasComplete = !!(prev && prev.completedAt);
  const complete = ids.length > 0 && hwm2 === ids.length;
  const playlist = (prev && prev.playlist) || { clips: {}, completedAt: null };
  const completedAt = wasComplete ? prev.completedAt : (complete ? nowMs : null);
  const masteredAt = (prev && prev.masteredAt)
    || (completedAt && playlist.completedAt ? nowMs : null);
  return {
    doc: {
      domain: situation.domain,
      domainSlug: situation.domainSlug,
      label: situation.label,
      sequence: situation.sequence,
      expressionsTotal: ids.length,
      taughtCount: taught,
      hwm2Count: hwm2,
      completedAt,
      masteredAt,
      playlist,
      masterclassIds: situation.masterclassIds || [],
    },
    justCompleted: complete && !wasComplete,
  };
}

// ── Streak + monthly freeze (Home spec §4.15) ───────────────────────────────
// 1 freeze granted per calendar month, auto-applied to a SINGLE missed day,
// not stackable. The streak advances at most once per local day.

/** Refill the monthly freeze if the grant month rolled. Non-stackable. */
export function grantMonthlyFreeze(streak, dateKey) {
  const s = { ...(streak || {}) };
  const month = monthOf(dateKey);
  if (s.freezeGrantMonth !== month) {
    s.freezeGrantMonth = month;
    // v6 streak ruling (2026-08-17): NO grace — the monthly grant is forced
    // to 0. The whole freeze code path (consumption branch in advanceStreak,
    // freezeChipVisible, freezeUsedDates) is kept intact for the week-6
    // revisit; flipping this 0 back to 1 re-enables all of it.
    s.freezesRemaining = 0;
  }
  return s;
}

/**
 * Whether today's activity earns the streak (Eddie ruling #5): a session that
 * had due-or-new work always earns; 자유 복습 earns ONLY when nothing was due
 * at all — it must never substitute for the real session.
 */
export function sessionEarnsStreak({ completedCount, dueCount, newCount, isFreeReview }) {
  if (!completedCount) return false;
  if (isFreeReview) return dueCount === 0 && newCount === 0;
  return true;
}

/**
 * Advance the streak for activity on `dateKey`. Idempotent per day. A gap of
 * exactly one missed day is bridged by an available freeze (consumed, missed
 * day recorded — the 48h chip reads freezeUsedDates); anything longer resets.
 */
/**
 * THE one streak-earning path (v8 refactor, ruled): both finish() and
 * finishFree() call this and nothing else — sessionEarnsStreak is the ONE
 * predicate for "a qualifying day", advanceStreak the ONE ledger writer.
 * No caller may pass `earned` directly; drift between the two paths is
 * structurally impossible.
 */
export function earnStreakDay(streak, dateKey, quals) {
  return advanceStreak(streak, dateKey, { earned: sessionEarnsStreak(quals) });
}

export function advanceStreak(streak, dateKey, { earned }) {
  let s = grantMonthlyFreeze(streak, dateKey);
  if (!earned) return s;
  if (s.lastActiveDate === dateKey) return s;
  if (s.lastActiveDate == null) {
    s.current = 1;
  } else {
    const gap = dayDiff(s.lastActiveDate, dateKey);
    if (gap <= 0) return s; // clock went backwards — never rewrite history
    if (gap === 1) {
      s.current = (s.current || 0) + 1;
    } else if (gap === 2 && (s.freezesRemaining || 0) > 0) {
      s = {
        ...s,
        freezesRemaining: s.freezesRemaining - 1,
        freezeUsedDates: [...(s.freezeUsedDates || []).slice(-11), addDays(dateKey, -1)],
      };
      s.current = (s.current || 0) + 1;
    } else {
      s.current = 1;
    }
  }
  s.longest = Math.max(s.longest || 0, s.current);
  s.lastActiveDate = dateKey;
  return s;
}

// ── Session interruption (brief §6, ruled) ──────────────────────────────────

/**
 * True when a saved in-progress session belongs to an earlier local day and
 * must dissolve SILENTLY: uncompleted items simply return to the queue under
 * normal rules (their state docs were never touched), completed items keep
 * their committed outcomes. No "you abandoned a session" messaging, ever.
 */
export function dissolves(inProgress, dateKey) {
  return !!(inProgress && inProgress.dateKey && inProgress.dateKey < dateKey);
}
